import React, { useState } from 'react';
import { FilterInputContainer } from '../../Commons/Facelift/Filters/styles';
import { ICustomer } from '../../../store/ducks/customers/types';
import { Select } from '@gerdau/hefesto/Select';
import { TSelectOptionProps } from '@gerdau/hefesto/Select/Select.types';
import { uppercaseOnlyFirstLetter } from '../../../utils/formatStrings';

interface CustomerSelectProps {
  title?: string;
  disabled: boolean;
  customers: ICustomer[];
  selectedCustomerId?: string;
  onSelect?(customerId: string): void;
  selectedCustomerIds?: string[];
  onSelectMultiple?(customerIds: string[]): void;
  isMultipleSelect?: boolean;
  canShowBlocks?: boolean;
  loading?: boolean;
  placeholder?: string;
  customStyles?: {
    selectTitleFontSize: string;
  };
}

const CustomerSelect: React.FC<CustomerSelectProps> = ({
  title,
  customers,
  onSelect,
  onSelectMultiple,
  selectedCustomerId,
  selectedCustomerIds,
  disabled,
  canShowBlocks = false,
  isMultipleSelect,
  placeholder
}) => {
  const [isSelectedAll, setIsSelectedAll] = useState<boolean>(false);

  const customersOptions: TSelectOptionProps[] = customers.map((customer) => ({
    label: customer.name,
    value: customer.customerId,
    disabled: canShowBlocks,
    descriptions: [
      `${uppercaseOnlyFirstLetter(
        customer.locality.address
      )} | ${uppercaseOnlyFirstLetter(customer.locality.city)} - ${
        customer.locality.state
      } | CEP ${customer.locality.zipCode}`,
      `BP ${customer.customerId}`,
      `CNPJ ${customer.taxNumber}`
    ]
  }));

  const customersIds: string[] = customers.map(
    (customer) => customer.customerId
  );

  const onCustomerSelect = (
    customersSelected: string | string[],
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    setSelectedCustomers: (customers: any) => void
  ) => {
    if (customersSelected) {
      setSelectedCustomers(customersSelected);
    }
  };

  return (
    <FilterInputContainer>
      <Select
        label={title ?? ''}
        placeholder={placeholder}
        name={'dropdownCustomers'}
        value={
          isMultipleSelect
            ? [
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                ...selectedCustomerIds!,
                ...(isSelectedAll ||
                selectedCustomerIds?.length === customersOptions.length
                  ? ['all']
                  : [])
              ]
            : selectedCustomerId
        }
        options={
          isMultipleSelect && customersOptions.length > 1
            ? [{ label: 'Selecionar todos', value: 'all' }, ...customersOptions]
            : customersOptions
        }
        isMultipleSelect={isMultipleSelect}
        disabled={disabled}
        onSelect={(value: string | string[]) => {
          if (isMultipleSelect && onSelectMultiple) {
            const onlyCustomersSelected = (value as string[]).filter(
              (selectedCustomer) => selectedCustomer !== 'all'
            );
            const isAllSelectedCustomer =
              onlyCustomersSelected.length === customersOptions.length;

            // Entra se todas as opções estiver marcada, e em seguida desmarca a opção de selecionar todos
            if (
              !(value as string[]).includes('all') &&
              isAllSelectedCustomer &&
              isSelectedAll
            ) {
              setIsSelectedAll(false);
              return onCustomerSelect([], onSelectMultiple);
            }

            // Entra caso identifique que todas as opções disponiveis foi selecionada e marca a opção selecionar todos
            if (isAllSelectedCustomer) {
              setIsSelectedAll(true);
              return onCustomerSelect([...customersIds], onSelectMultiple);
            }

            // Entra caso a opção selecionar todos foi marcada
            if ((value as string[]).includes('all') && !isSelectedAll) {
              setIsSelectedAll(true);
              return onCustomerSelect([...customersIds], onSelectMultiple);
            }

            // Selecionou uma opção diferente de selecionar todos e não foram todos selecionados
            setIsSelectedAll(false);
            return onCustomerSelect(
              [...onlyCustomersSelected],
              onSelectMultiple
            );
          }

          if (!isMultipleSelect && onSelect) {
            onCustomerSelect(value as string[], onSelect);
          }
        }}
      ></Select>
    </FilterInputContainer>
  );
};

export default CustomerSelect;
